import { useEffect, useState } from 'react';
import { Button } from '../../components/ui';
import { FEEDBACK_REASON_MAX } from './feedback';
import type { Grade, MessageFeedback } from './feedback';

/**
 * Thumbs up / down under an agent's message (Messaging Drop 3b-2,
 * plan-drop3-extras.md F2-F4; mockup/Main.dc.html:101-104).
 *
 * Visible to the room (Kyu, 26 Sept 11:35): everyone who can see the message
 * sees an up chip and a down chip with their counts, in the same chip style
 * as the quick reactions row (CommentReactions.tsx), and every "What was
 * wrong?" note with the person's name. The chip's hover title and label name
 * who gave it ("Thumbs up: Ana, Mico, including you").
 *
 * Someone who may grade (canGrade: a TAP admin or member, not the author)
 * clicks a chip to add, change or remove their own thumb, exactly as before
 * (feedbackActionFor); their own chip shows "on". A thumbs down opens one
 * optional line, "What was wrong?". Only they see "Your thumbs train the
 * agents".
 *
 * Someone who may not grade (a TAP Viewer, or the author) gets plain chips
 * with no buttons and no note box, and a chip nobody has used is hidden.
 *
 * Names and notes are only ever React text, never HTML.
 */

function ThumbIcon({ down }: { down: boolean }) {
  // Same paths as the mockup (Main.dc.html:102-103).
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path
        d={
          down
            ? 'M17 13V4h3v9zM17 13l-4 8c-1.5 0-2.5-1-2.5-2.5V15h-5a2 2 0 01-2-2.3l1.2-7A2 2 0 016.7 4H17'
            : 'M7 11v9H4v-9zM7 11l4-8c1.5 0 2.5 1 2.5 2.5V9h5a2 2 0 012 2.3l-1.2 7A2 2 0 0117.3 20H7'
        }
      />
    </svg>
  );
}

const EMPTY: MessageFeedback = { up: [], down: [], notes: [], mine: undefined };

/**
 * "Thumbs up: Ana, Mico", ", including you" when yours is among them, "Thumbs
 * up: you" when it is only yours, and just "Thumbs up" when nobody has.
 */
export function thumbChipLabel(
  grade: Grade,
  userIds: string[],
  myId: string,
  nameOf: (userId: string) => string,
): string {
  const title = grade === 'up' ? 'Thumbs up' : 'Thumbs down';
  const mine = myId !== '' && userIds.includes(myId);
  const others = userIds.filter((id) => id !== myId).map(nameOf);
  if (others.length === 0) {
    return mine ? `${title}: you` : title;
  }
  return `${title}: ${others.join(', ')}${mine ? ', including you' : ''}`;
}

export function MessageThumbs({
  feedback,
  myId,
  nameOf,
  canGrade,
  pending,
  error,
  onGrade,
  onReason,
}: {
  /** Every grade and note on this message the database let us read. */
  feedback: MessageFeedback | undefined;
  myId: string;
  /** display_name, then email, then "Someone" (MessagesPage builds it). */
  nameOf: (userId: string) => string;
  /** A TAP admin or member who did not write the message. */
  canGrade: boolean;
  pending: boolean;
  error: string;
  onGrade: (grade: Grade) => void;
  onReason: (reason: string) => void;
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const summary = feedback ?? EMPTY;
  const mine = summary.mine;

  // A thumbs down with no reason yet asks once; changing away closes it.
  useEffect(() => {
    if (mine?.grade !== 'down') {
      setAsking(false);
    }
  }, [mine?.grade]);

  function grade(next: Grade) {
    const turningDown = next === 'down' && mine?.grade !== 'down';
    onGrade(next);
    if (turningDown) {
      setReason('');
      setAsking(true);
    }
  }

  const chips = (['up', 'down'] as const)
    .map((which) => ({ which, ids: summary[which] }))
    .filter((chip) => canGrade || chip.ids.length > 0);

  if (!canGrade && chips.length === 0 && summary.notes.length === 0) {
    return null;
  }

  return (
    <div className="message-thumbs">
      {chips.length === 0 && !canGrade ? null : (
        <div className="message-thumbs-row" role="group" aria-label="Thumbs">
          {chips.map(({ which, ids }) => {
            const label = thumbChipLabel(which, ids, myId, nameOf);
            const on = mine?.grade === which;
            const count =
              ids.length === 0 ? null : (
                <span className="comment-reaction-count">{ids.length}</span>
              );
            return canGrade ? (
              <button
                key={which}
                type="button"
                className={`comment-reaction message-thumb${on ? ' comment-reaction--mine message-thumb--on' : ''}`}
                aria-label={label}
                aria-pressed={on}
                title={label}
                disabled={pending}
                onClick={() => grade(which)}
              >
                <ThumbIcon down={which === 'down'} />
                {count}
              </button>
            ) : (
              <span
                key={which}
                className="comment-reaction message-thumb"
                aria-label={label}
                title={label}
              >
                <ThumbIcon down={which === 'down'} />
                {count}
              </span>
            );
          })}
          {canGrade ? (
            <span className="message-thumbs-note">Your thumbs train the agents</span>
          ) : null}
        </div>
      )}
      {summary.notes.length === 0 ? null : (
        <ul className="message-thumbs-notes" aria-label="What was wrong">
          {summary.notes.map((note) => (
            <li key={note.userId} className="message-thumbs-notes-item">
              <span className="message-thumbs-notes-name">{nameOf(note.userId)}</span>:{' '}
              {note.reason}
            </li>
          ))}
        </ul>
      )}
      {canGrade && asking && mine?.grade === 'down' ? (
        <div className="message-thumbs-reason">
          <label className="message-thumbs-reason-label">
            <span>What was wrong?</span>
            <input
              type="text"
              className="message-thumbs-reason-input"
              value={reason}
              maxLength={FEEDBACK_REASON_MAX}
              placeholder="Optional"
              onChange={(event) => setReason(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  onReason(reason);
                  setAsking(false);
                }
              }}
            />
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setAsking(false);
            }}
          >
            Skip
          </Button>
          <Button
            size="sm"
            disabled={pending || reason.trim() === ''}
            onClick={() => {
              onReason(reason);
              setAsking(false);
            }}
          >
            Save
          </Button>
        </div>
      ) : null}
      {error === '' ? null : (
        <p className="message-thumbs-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
